import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  TicketFilterOptionsSelector,
  TicketsLoadingStateSelector,
  TicketsParamsSelector,
  TicketsSelector,
} from '../../../../store/tickets/selectors';
import { UserDataSelector } from 'store/user/selector';
import {
  changeCwTicketFilterParams,
  changeTicketFilterParams,
  changeCustomTicketStatusFilter,
  clearTickets,
} from '../../../../store/tickets';
import { customFilterNames, ticketFilters } from './constants';
import { ITicketsFilter } from './types';
import { ICustomFilterTicketStatus } from 'services/user/types';
import { LOCAL_STORAGE_PSA } from '../../../../constants/storage';
import { PsaTypes } from '../../../../services/config/types';
import { ITicketFilterOptions, ITicketsParams } from '../../../../services/tickets/types';
import { ThunkGetTickets, ThunkGetTicketsFiltersOptions } from '../../../../store/tickets/thunks';

export const useTicketsFilters = (resetTrigger?: boolean) => {
  const ticketFilterOptions = useAppSelector(TicketFilterOptionsSelector);
  const params = useAppSelector(TicketsParamsSelector);
  const tickets = useAppSelector(TicketsSelector);
  const loading = useAppSelector(TicketsLoadingStateSelector);
  const { useCustomTicketStatusFilter, customFilterTicketStatuses } =
    useAppSelector(UserDataSelector);

  const dispatch = useAppDispatch();

  const [statuses, setStatuses] = useState<ICustomFilterTicketStatus[]>([]);
  const [activeIndex, setActiveIndex] = useState(1);

  const isCustomFilter = !ticketFilterOptions.useDefaultsStatuses;

  const onFilterClick = useCallback(
    (
      filter: ITicketsFilter,
      filterOptions: ITicketFilterOptions,
      queryParams: ITicketsParams,
      isCustom: boolean,
    ) => {
      const psa = localStorage.getItem(LOCAL_STORAGE_PSA);
      if (psa === PsaTypes[PsaTypes.Connectwise] && !(isCustom ?? isCustomFilter)) {
        dispatch(changeCwTicketFilterParams(filter.isClosed));
        dispatch(
          ThunkGetTickets({ params: { ...queryParams, isClosed: filter.isClosed ?? null } }),
        );
        return;
      }

      const filterValue = filter.filterValue ? (filterOptions[filter.filterValue] as string) : '';
      const payload: { statusId: string | null; ticketStatusId: number[] | string[] } = {
        statusId: filterValue,
        ticketStatusId: [],
      };

      if (isCustom) {
        payload.statusId = null;
        if (filterValue) {
          if (psa === PsaTypes[PsaTypes.SuperOps]) {
            payload.ticketStatusId = filterValue.split(',').map(e => e.toString());
          } else {
            payload.ticketStatusId = filterValue.split(',').map(e => +e);
          }
        }
      }

      dispatch(ThunkGetTickets({ params: { ...queryParams, ...payload } }));
      dispatch(changeTicketFilterParams(payload));
    },
    [dispatch, isCustomFilter],
  );

  useEffect(() => {
    dispatch(ThunkGetTicketsFiltersOptions())
      .unwrap()
      .then(res => {
        const initial = {
          ...ticketFilters[1],
          filterValue: !res.useDefaultsStatuses
            ? (customFilterNames[1] as keyof ITicketFilterOptions)
            : ticketFilters[1].filterValue,
        };
        onFilterClick(
          initial,
          res,
          {
            page: 0,
            pageSize: 10,
            search: '',
            statusId: '',
            ticketStatusId: [],
            isClosed: null,
            _sort: 'id',
            _order: 1,
          },
          !res.useDefaultsStatuses,
        );
      });
  }, [dispatch]);

  useEffect(() => {
    if (typeof resetTrigger === 'boolean') {
      const initial = {
        ...ticketFilters[1],
        filterValue: isCustomFilter
          ? (customFilterNames[1] as keyof ITicketFilterOptions)
          : ticketFilters[1].filterValue,
      };

      onFilterClick(
        initial,
        ticketFilterOptions,
        {
          page: 0,
          pageSize: 10,
          search: '',
          statusId: '',
          ticketStatusId: [],
          isClosed: null,
          _sort: 'id',
          _order: 1,
        },
        isCustomFilter,
      );
      setActiveIndex(1)
    }
  }, [resetTrigger, isCustomFilter, ticketFilterOptions]);

  useEffect(() => {
    return () => {
      dispatch(clearTickets());
    };
  }, [dispatch]);

  const onSelectFilter = useCallback(
    (idx: number) => {
      setActiveIndex(idx);
      const base = ticketFilters[idx];
      const computed = {
        ...base,
        filterValue: isCustomFilter
          ? (customFilterNames[idx] as keyof ITicketFilterOptions)
          : base.filterValue,
      };
      onFilterClick(computed, ticketFilterOptions, params, isCustomFilter);
    },
    [isCustomFilter, onFilterClick, ticketFilterOptions, params],
  );

  const onSaveCustomFilterTicketStatuses = useCallback(() => {
    const statusIds = statuses.map(status => +status.value);
    dispatch(changeCustomTicketStatusFilter(statusIds));
  }, [statuses, dispatch]);

  const onStatusesChange = useCallback(
    (value: ICustomFilterTicketStatus[]) => {
      setStatuses(value);
      if (value.length === 0) {
        dispatch(changeCustomTicketStatusFilter([]));
      }
    },
    [dispatch],
  );

  return {
    statuses,
    activeIndex,
    useCustomTicketStatusFilter,
    customFilterTicketStatuses,
    ticketFilters,
    onSelectFilter,
    onSaveCustomFilterTicketStatuses,
    onStatusesChange,
    tickets,
    loading
  };
};
